import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useState } from "react";
import {
  fetchShareableTree,
  ShareableDevice,
  ShareableTree,
  ShareResourceType,
  shareResource,
  unshareResource,
} from "../../../api/partnership";
import { Button } from "../../common/Button";
import { Modal, ModalActions } from "../../common/Modal";

type SharePartnershipModalProps = {
  isOpen: boolean;
  onClose: () => void;
  partnershipId: number | null;
  /** Nom de l'entreprise partenaire, pour que l'utilisateur voie à qui il donne accès. */
  companyName: string;
};

/**
 * Partage de ressources avec un partenaire.
 *
 * Arborescence à trois niveaux — élevage > bâtiment > produit — car le partage peut porter
 * sur n'importe lequel. Cocher un ÉLEVAGE couvre tout ce qu'il contient, **y compris les
 * bâtiments et produits ajoutés plus tard** : c'est le backend qui résout à la lecture,
 * rien n'est figé au moment du clic.
 *
 * Chaque case envoie immédiatement l'appel (pas de bouton « Enregistrer ») : l'action est
 * unitaire et réversible, une validation globale laisserait croire qu'on peut annuler.
 *
 * Le partage vaut pour TOUTE l'entreprise partenaire, pas pour une personne — c'est dit
 * explicitement, sans quoi l'utilisateur croirait donner un accès nominatif.
 */
export const SharePartnershipModal = ({
  isOpen,
  onClose,
  partnershipId,
  companyName,
}: SharePartnershipModalProps) => {
  const [tree, setTree] = useState<ShareableTree>({
    breedings: [],
    orphan_devices: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Record<number, boolean>>({});

  const load = useCallback(async () => {
    if (!partnershipId) return;
    setLoading(true);
    const result = await fetchShareableTree(partnershipId);
    setTree(result.tree);
    setError(result.error);
    setLoading(false);
  }, [partnershipId]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const handleToggle = async (
    resourceType: ShareResourceType,
    id: number,
    currentlyShared: boolean
  ) => {
    if (!partnershipId) return;

    const key = `${resourceType}-${id}`;
    setBusyKey(key);
    setError(null);
    const result = currentlyShared
      ? await unshareResource(partnershipId, resourceType, id)
      : await shareResource(partnershipId, resourceType, id);
    setBusyKey(null);

    if (!result.success) {
      setError(result.error);
      return;
    }

    // Rechargement complet plutôt qu'une mise à jour locale : cocher un élevage change
    // l'accès effectif de tous ses descendants, et l'écran doit le refléter.
    await load();
  };

  const DeviceRow = ({
    device,
    depth,
    coveredByParent,
  }: {
    device: ShareableDevice;
    depth: number;
    coveredByParent: boolean;
  }) => (
    <label
      style={{ paddingLeft: `${depth * 1.25}rem` }}
      className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors ${
        device.shared
          ? "border-secondary bg-grey-light"
          : "border-grey-medium hover:bg-grey-light"
      } ${busyKey === `device-${device.id}` ? "opacity-50" : ""} ${
        coveredByParent ? "opacity-60" : ""
      }`}
    >
      <input
        type="checkbox"
        checked={device.shared || coveredByParent}
        // Déjà couvert par un niveau supérieur : décocher ici n'aurait aucun effet
        // (l'accès viendrait toujours du parent), on désactive plutôt que de mentir.
        disabled={busyKey === `device-${device.id}` || coveredByParent}
        onChange={() => handleToggle("device", device.id, device.shared)}
        className="w-4 h-4 accent-secondary cursor-pointer"
      />
      <div className="min-w-0">
        <p className="text-default-text truncate">
          {device.name || device.uuid}
        </p>
        <p className="text-xs text-tertiary truncate">
          {device.type} · {device.uuid}
        </p>
      </div>
    </label>
  );

  // `?? []` partout : Go sérialise une slice vide NIL en `null` (et non `[]`). Le backend
  // s'en protège désormais, mais un seul oubli côté serveur suffirait à blanchir l'écran.
  const hasContent =
    (tree.breedings ?? []).length > 0 || (tree.orphan_devices ?? []).length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Partager mes ressources"
      icon="handshake"
      maxWidth="lg"
    >
      <div className="py-2">
        <p className="text-tertiary mb-4">
          Choisissez ce à quoi <strong>{companyName}</strong> aura accès. Cocher
          un élevage ou un bâtiment couvre tout ce qu'il contient, y compris les
          produits ajoutés plus tard. Tous les collaborateurs de cette entreprise
          pourront les consulter.
        </p>

        {error && (
          <div className="mb-3 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-tertiary">Chargement…</p>
        ) : !hasContent ? (
          <div className="py-6 text-center text-tertiary">
            <FontAwesomeIcon icon="cogs" size="2x" className="mb-3" />
            <p>Vous n'avez aucune ressource à partager pour le moment.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2 max-h-96 overflow-y-auto">
            {(tree.breedings ?? []).map((breeding) => (
              <div key={breeding.id} className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setCollapsed((prev) => ({
                        ...prev,
                        [breeding.id]: !prev[breeding.id],
                      }))
                    }
                    aria-label="Déplier ou replier cet élevage"
                    className="text-tertiary hover:text-default-text p-1"
                  >
                    <FontAwesomeIcon
                      icon="chevron-down"
                      className={
                        collapsed[breeding.id] ? "-rotate-90 transition" : "transition"
                      }
                    />
                  </button>
                  <label
                    className={`flex-1 flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors ${
                      breeding.shared
                        ? "border-secondary bg-grey-light"
                        : "border-grey-medium hover:bg-grey-light"
                    } ${busyKey === `breeding-${breeding.id}` ? "opacity-50" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={breeding.shared}
                      disabled={busyKey === `breeding-${breeding.id}`}
                      onChange={() =>
                        handleToggle("breeding", breeding.id, breeding.shared)
                      }
                      className="w-5 h-5 accent-secondary cursor-pointer"
                    />
                    <span className="font-oxaniumMedium text-default-text truncate">
                      {breeding.name}
                    </span>
                    <span className="ml-auto text-xs text-tertiary shrink-0">
                      Élevage
                    </span>
                  </label>
                </div>

                {!collapsed[breeding.id] &&
                  (breeding.buildings ?? []).map((building) => (
                    <div key={building.id} className="flex flex-col gap-1">
                      <label
                        style={{ paddingLeft: "1.25rem" }}
                        className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors ${
                          building.shared
                            ? "border-secondary bg-grey-light"
                            : "border-grey-medium hover:bg-grey-light"
                        } ${
                          busyKey === `building-${building.id}` ? "opacity-50" : ""
                        } ${breeding.shared ? "opacity-60" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={building.shared || breeding.shared}
                          disabled={
                            busyKey === `building-${building.id}` || breeding.shared
                          }
                          onChange={() =>
                            handleToggle("building", building.id, building.shared)
                          }
                          className="w-4 h-4 accent-secondary cursor-pointer"
                        />
                        <span className="text-default-text truncate">
                          {building.name}
                        </span>
                        <span className="ml-auto text-xs text-tertiary shrink-0">
                          Bâtiment
                        </span>
                      </label>

                      {(building.devices ?? []).map((device) => (
                        <DeviceRow
                          key={device.id}
                          device={device}
                          depth={2}
                          coveredByParent={breeding.shared || building.shared}
                        />
                      ))}
                    </div>
                  ))}
              </div>
            ))}

            {(tree.orphan_devices ?? []).length > 0 && (
              <div className="mt-2">
                <p className="text-sm text-tertiary mb-1">
                  Produits sans bâtiment
                </p>
                <div className="flex flex-col gap-1">
                  {(tree.orphan_devices ?? []).map((device) => (
                    <DeviceRow
                      key={device.id}
                      device={device}
                      depth={0}
                      coveredByParent={false}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <ModalActions>
          <Button onClick={onClose}>Fermer</Button>
        </ModalActions>
      </div>
    </Modal>
  );
};
